import { inject, computed } from '@angular/core';
import { Router } from '@angular/router';
import { GenericResponseTable } from '@core/models/shared';
import { GenericTableRequest } from '@core/models/shared/generic-table-request.model';
import { ApiService } from '@core/services/api.service';
import { IDRISORSEFLUSSO, IDRISORSESIDENAV } from '@core/utils/const';
import {
  signalStore,
  withHooks,
  withMethods,
  withState,
  withComputed,
  patchState,
  withProps,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { pipe, tap, switchMap, firstValueFrom, catchError, EMPTY } from 'rxjs';
import { AuthStore } from '@core/store/auth/auth.store';
import { AttrezzatureByUtenzaList, CondominioDTO, ListComuni, ListCondominio, ListUtenza, ListUtenzeCondomino } from '@core/models/api/swagger-user';
import { canWrite } from '@core/utils/utils';
import { withReset } from '@angular-architects/ngrx-toolkit';
import { ModalAssociaUtenzaComponent } from './associa-nuove-utenze/modal-associa-utenza/modal-associa-utenza.component';

type CondominiState = {
  filter: any;
  filterDaAssegnare: any;
  utenzeListDaAssegnare: ListUtenza[];
  condominioList: ListCondominio[];
  condominioListByIdUtenza: ListUtenzeCondomino[];
  condominioById: CondominioDTO | null;
  comuniNascitaOptions: ListComuni[];
  comuniResidenzaOptions: ListComuni[];
  listAttrezzaturaCondomini: AttrezzatureByUtenzaList[],
  loader: boolean;
  loaderList: boolean;
  totalElements: number;
  totalElementsByIdUtenza: number;
  totalElementsDaAssegnare: number;
};

export const initialCondominiStore: CondominiState = {
  filter: {
    condominio: '',
    cfPiva: '',
    indirizzo: '',
    esponente: '',
    idOperatore: '',
    idUtenza: '',
    idUtenzaCondominiale: '',
    nsoggetto: '',
    nutenze: '',
    stato: '',
    size: 10,
    page: 0,
    sort: '',
  },
  filterDaAssegnare: {
    search: "",
    idEsterno: "",
    idInterno: '',
    indirizzo: '',
    soggetto: '',
    attrezzatura: false,
    cap: '',
    civico: '',
    comune: '',
    metriQuadri: '',
    size: 10,
    page: 0,
    sort: ''
  },
  utenzeListDaAssegnare: [],
  condominioList: [],
  condominioListByIdUtenza: [],
  condominioById: null,
  comuniNascitaOptions: [],
  comuniResidenzaOptions: [],
  listAttrezzaturaCondomini: [],
  loader: false,
  loaderList: false,
  totalElements: 0,
  totalElementsByIdUtenza: 0,
  totalElementsDaAssegnare: 0,
};

export const CondominiStore = signalStore(
  { providedIn: 'root' },
  withState(initialCondominiStore),
  withReset(),
  withProps(() => ({
    messageService: inject(MessageService),
    apiService: inject(ApiService),
    router: inject(Router),
    translateService: inject(TranslateService),
    authStore: inject(AuthStore),
    showTipologiaCodice: (tipologiaCodice: string, field: 'tipologia' | 'codice'): string => {
      if (tipologiaCodice.length) {
        const tipoSplit: string[] = tipologiaCodice.split(' ');

        switch (field) {
          case 'tipologia':
            return tipoSplit[2];

          case 'codice':
            return tipoSplit[0];

          default: return '--';
        }
      } else {
        return '--'
      }

    },
  })),
  withComputed((store) => ({
    filterMap: computed(() => store.filter()),
    canWrite: computed(() =>
      canWrite(IDRISORSESIDENAV.anagrafica_soggetti, IDRISORSEFLUSSO.condominio) ||
      canWrite(IDRISORSESIDENAV.distribuzione_anagrafica_soggetti, IDRISORSEFLUSSO.distribuzione_condominio)
    )
  })),
  withMethods((store) => ({
    clearTableData() {
      patchState(store, { condominioList: [], totalElements: 0 })
    },
    resetComuniResidenzaOptions: () => {
      patchState(store, { comuniResidenzaOptions: [] })
    },
    setComuniResidenzaOptions: (options: ListComuni[]) => {
      patchState(store, { comuniResidenzaOptions: options })
    },
    resetToFirstPage: () => {
      const resetFilter = { currentFilter: null, page: 0, search: '', size: 10 };
      patchState(store, { filter: resetFilter });
    },
    updateFilters: (formFilters: GenericTableRequest) => {
      patchState(store, { filter: formFilters });
    },
    updateFiltersDaAssegnare: (formFilters: GenericTableRequest) => {
      patchState(store, { filterDaAssegnare: formFilters })
    },
    fetchAttrezzatureCondomini: async (idCondominio: number) => {
      try {
        const response = await firstValueFrom(
          store.apiService.swaggerEquipmentService.getAttrezzatureByUtenza(idCondominio)
        );
        const mapAttrezzatureConTag: AttrezzatureByUtenzaList[] = response.conTag.map((conCodice) => {
          const attrUtenzaCode: AttrezzatureByUtenzaList = {
            attrezzature: conCodice.descrizione,
            tipoAtrezzatura: conCodice.tipoAtrezzatura,
            codice: store.showTipologiaCodice(conCodice.codice, 'codice'),
            colore: '',
            dataConsegna: conCodice.dataConsegna as string,
            desTipo: '',
            id: conCodice.id,
            idDettaglio: 0,
            idTipologiaAttrezzatura: conCodice.idTipologiaAttrezzatura,
            matricola: conCodice.matricola,
            quantita: 1,
            tipologia: store.showTipologiaCodice(conCodice.codice, 'tipologia'),
            tipologiaCodice: 'Codice',
            volume: '',
            descrizione: conCodice.descrizione
          }
          return attrUtenzaCode;
        })

        const mapAttrezzatureSenzaTag: AttrezzatureByUtenzaList[] = response.senzaTag.map((senzaCodice) => {
          const attrUtenzaCode: AttrezzatureByUtenzaList = {
            attrezzature: senzaCodice.tipologia,
            descrizione: senzaCodice.descrizione,
            tipoAtrezzatura: senzaCodice.desTipo!,
            codice: '',
            colore: '',
            dataConsegna: senzaCodice.dataConsegna as string,
            desTipo: '',
            id: senzaCodice.id,
            idDettaglio: 0,
            idTipologiaAttrezzatura: senzaCodice.idTipologiaAttrezzatura!,
            matricola: '',
            quantita: senzaCodice.quantita,
            tipologia: senzaCodice.codice,
            tipologiaCodice: 'Senza codice',
            volume: ''
          }
          return attrUtenzaCode;
        })

        const listAttrezzature: AttrezzatureByUtenzaList[] = mapAttrezzatureConTag.concat(mapAttrezzatureSenzaTag)

        patchState(store, {
          listAttrezzaturaCondomini: listAttrezzature,
        });
      }
      catch {
        patchState(store);
      }
    },
    getCondominioList: rxMethod<any>(
      pipe(
        tap(() => patchState(store, { loaderList: true })),
        switchMap((filter: GenericTableRequest) =>
          store.apiService.swaggerUserService.fetchCondominiList(filter).pipe(
            tap((response: GenericResponseTable<ListCondominio>) => {
              patchState(store, {
                condominioList: response.content,
                totalElements: response.totalElements,
                loaderList: false,
              });
            }),
            catchError(() => { patchState(store, { loaderList: false }); return EMPTY })
          )
        ),
      )
    ),
    getUtenzeListDaAssegnare: rxMethod<any>(
      pipe(
        tap(() => patchState(store, { loader: true })),
        switchMap((filterDaAssegnare: GenericTableRequest) =>
          store.apiService.swaggerUserService.fetchUtenzeDaAssegnare(filterDaAssegnare).pipe(
            tap((response: GenericResponseTable<ListUtenza>) => {
              patchState(store, { utenzeListDaAssegnare: response.content, totalElementsDaAssegnare: response.totalElements, loader: false });
            }),
            catchError(() => { patchState(store, { loader: false }); return EMPTY })
          )
        ),
      )),
    clearUtenzeListDaAssegnare() {
      patchState(store, { utenzeListDaAssegnare: [], totalElementsDaAssegnare: 0, loader: false });

    },
    // TODO chiamata identica ripetuta, perchè?
    // lista comuni di residenza usata in soggetti
    getComuniResidenzaOptions: async (
      comuniPayload: GenericTableRequest
    ) => {
      try {
        patchState(store, { loader: true });
        const response = await firstValueFrom(
          store.apiService.swaggerUserService.fetchListComuni(comuniPayload)
        );
        if (response) {
          patchState(store, { comuniResidenzaOptions: response.content, loader: false });
        }
      } catch {
        patchState(store, { loader: false });
      }
    },
    addCondominio: async (requestBody: CondominioDTO) => {
      try {
        patchState(store, { loader: true });

        if (requestBody.cf) {
          requestBody.cf = requestBody.cf.trim().toUpperCase();
        }

        const response = await firstValueFrom(
          store.apiService.swaggerUserService.addCondominio(requestBody)
        );
        if (response) {
          patchState(store, {
            condominioById: response.responseObj,
            loader: false,
          });
          store.router.navigate([
            '/anagrafica-soggetti/dettaglio-condominio/' +
            response.responseObj.id,
          ]);
          store.messageService.add({
            severity: 'success',
            summary: store.translateService.instant(
              'shared_message_api.success'
            ),
            detail: store.translateService.instant(
              'shared_message_api.message_success'
            ),
          });
        }
      } catch {
        patchState(store, { loader: false });
      }
    },
    updateCondominio: async (requestBody: CondominioDTO) => {
      try {
        patchState(store, { loader: true });

        if (requestBody.cf) {
          requestBody.cf = requestBody.cf.trim().toUpperCase();
        }

        // Chiamata al servizio per inviare i dati (POST)
        const response = await firstValueFrom(
          store.apiService.swaggerUserService.updateCondominio(requestBody)
        );
        if (response) {
          patchState(store, {
            condominioById: response.responseObj,
            loader: false,
          });
          store.router.navigate([
            '/anagrafica-soggetti/dettaglio-condominio/' +
            response.responseObj.id,
          ]);
          store.messageService.add({
            severity: 'success',
            summary: store.translateService.instant(
              'shared_message_api.success'
            ),
            detail: store.translateService.instant(
              'shared_message_api.message_success'
            ),
          });
        }
      } catch (er) {
        patchState(store, { loader: false });
        throw er;
      }
    },
    detailsCondominio: async (idCondominio: number) => {
      try {
        patchState(store, { loader: true });
        const response = await firstValueFrom(
          store.apiService.swaggerUserService.getCondominioById(idCondominio)
        );
        if (response) {
          if (response.responseObj?.cf) {
            response.responseObj.cf = response.responseObj.cf.trim().toUpperCase();
          }

          patchState(store, {
            condominioById: response.responseObj,
            loader: false,
          });
        }
      } catch {
        patchState(store, { loader: false });
      }
    },
    disassociateUtenza: async (idCondominio: number, idUtenza: number) => {
      try {
        patchState(store, { loader: true });
        // Chiamata al servizio per ottenere i dettagli del cliente (GET)
        const response = await firstValueFrom(
          store.apiService.swaggerUserService.dissociaUtenzaCondominio(
            idCondominio,
            idUtenza
          )
        );
        if (response) {
          patchState(store, { loader: false });
          store.messageService.add({
            severity: 'success',
            summary: store.translateService.instant(
              'shared_message_api.success'
            ),
            detail: store.translateService.instant(
              'shared_message_api.message_success'
            ),
          });
        }
      } catch {
        patchState(store, { loader: false });
      }
    },
    dissociateAllUsers: async (idCondominio: number) => {
      try {
        patchState(store, { loader: true });
        const response = await firstValueFrom(
          store.apiService.swaggerUserService.dissociaAllUtenze(idCondominio)
        );
        if (response) {
          patchState(store, { loader: false });
          store.messageService.add({
            severity: 'success',
            summary: store.translateService.instant(
              'shared_message_api.success'
            ),
            detail: store.translateService.instant(
              'shared_message_api.message_success'
            ),
          });
        }
      } catch {
        patchState(store, { loader: false });
      }
    },
    getUtentiByIdCondominio: async (idCondominio: number, filter: GenericTableRequest) => {
      try {
        patchState(store, { loader: true });
        // Chiamata al servizio per ottenere i dettagli del cliente (GET)
        const response = await firstValueFrom(
          store.apiService.swaggerUserService.fetchUtenzeListByIdCondominio(idCondominio, filter)
        );
        if (response) {
          patchState(store, {
            condominioListByIdUtenza: response.content,
            totalElementsByIdUtenza: response.totalElements,
            loader: false,
          });
        }
      } catch {
        patchState(store, { loader: false });
      }
    },
    associaUtenze: async (
      idCondominio: number,
      idUtenze: number[],
      dialogService?: DialogService,
    ) => {
      try {
        patchState(store, { loader: true });
        // Chiamata al servizio per associare le utenze al condominio
        const response = await firstValueFrom(
          store.apiService.swaggerUserService.assegnaUtenzeCondominio(
            idCondominio,
            idUtenze
          )
        );
        if (response) {
          patchState(store, {
            loader: false,
            filterDaAssegnare: {
              ...store.filterDaAssegnare(),
              page: 0,
            },
          });

          if (dialogService) {
            dialogService.open(ModalAssociaUtenzaComponent, {
              header: 'Utenze',
              width: '400px',
              closable: true,
              modal: true,
              contentStyle: { overflow: 'auto' },
              baseZIndex: 90000,
              draggable: false,
              data: {
                subtitle: 'Sono state associate al codominio',
                labelButton: 'Vai al codominio',
                path: '/anagrafica-soggetti/dettaglio-condominio',
                id: idCondominio,
                nUtenza: idUtenze.length,
              }
            });
          }
        }
      } catch {
        patchState(store, { loader: false });
      }
    },
    exportCondomini: async (reqeustBody: GenericTableRequest) => {
      try {
        let response = await firstValueFrom(
          store.apiService.swaggerUserService.exportCondomini(reqeustBody)
        );

        store.messageService.add({
          severity: 'success',
          summary: 'Export Condomini',
          detail: response.response
        });
      }
      catch {
        patchState(store);
      }
    },
  })),
  withHooks({
    onInit: (store) => {
      store.getCondominioList(store.filter);
      store.getUtenzeListDaAssegnare(store.filterDaAssegnare);
    },
  })
);
